import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { SITE_URL } from '@/constants';

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  category: string;
  children: React.ReactNode;
  schemaJson: Record<string, unknown>;
  faqSchema?: Record<string, unknown>;
  breadcrumbs: Array<{ name: string; path: string }>;
}

export default function ArticleLayout({ title, subtitle, category, children, schemaJson, faqSchema, breadcrumbs }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <section className="pt-28 pb-12 md:pt-32 md:pb-16 bg-background-100">
          <div className="section-container">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6 flex-wrap">
                {breadcrumbs.map((crumb, i) => (
                  <span key={crumb.path} className="flex items-center gap-2">
                    {i > 0 && <i className="ri-arrow-right-s-line"></i>}
                    {i < breadcrumbs.length - 1 ? (
                      <Link to={crumb.path} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                        {crumb.name}
                      </Link>
                    ) : (
                      <span className="text-primary-600 font-medium whitespace-nowrap">{crumb.name}</span>
                    )}
                  </span>
                ))}
              </nav>

              <span className="inline-block text-xs font-medium bg-primary-100 text-primary-700 px-3 py-1 rounded-full mb-4">
                {category}
              </span>

              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-5">
                {title}
              </h1>

              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                {subtitle}
              </p>

              <div className="flex items-center gap-4 text-xs text-foreground-400">
                <span>Updated: June 2026</span>
                <span>&middot;</span>
                <span>8-12 min read</span>
              </div>
            </div>
          </div>
        </section>

        <article className="py-12 md:py-16">
          <div className="section-container">
            <div className="max-w-3xl mx-auto">
              {children}

              {/* Back to Home */}
              <div className="text-center pt-10 border-t border-background-200/70 mt-12">
                <p className="text-sm text-foreground-500 mb-4">Ready to start exploring the best Sugargoo finds?</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-home-line"></i>
                    Back to Homepage
                  </Link>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-foreground-900 text-background-50 px-6 py-3 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
                  >
                    View Spreadsheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          ...schemaJson,
          url: `${SITE_URL}${window.location.pathname}`,
          '@id': `${SITE_URL}${window.location.pathname}#article`,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${SITE_URL}${window.location.pathname}`,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Sugargoo Spreadsheet Watch',
            url: SITE_URL,
            logo: {
              '@type': 'ImageObject',
              url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
            },
          },
        }),
      }} />
      {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((crumb, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: crumb.name,
              item: `${SITE_URL}${crumb.path}`,
            })),
          }),
        }}
      />
    </div>
  );
}